import { useList } from '@refinedev/core';
import { Button, Select, Spin } from 'antd'
import { useEffect, useState } from 'react'
import debounce from 'lodash/debounce';
import type { CSSProperties } from "react";

interface UserOption {
    value: string;
    label: string;
}

function UserSearchSelect({ onChange, userType, style, onInviteRequested, inviteButtonLabel } :
    {
        onChange: (userID: string) => void,
        userType: ("ADMIN" | "SUPER_ADMIN" | "EMPLOYEE" | "USER")[],
        style?: CSSProperties,
        onInviteRequested?: (emailOrQuery: string) => void,
        inviteButtonLabel?: string,
    }) {
    const [userOptions, setUserOptions] = useState<UserOption[]>([]);
    const [searchText, setSearchText] = useState('');

    const { data, isLoading, isFetching } = useList({
        resource: "user_select",
        meta: {
          q: searchText,
          userType
        },
        queryOptions: {
            enabled: searchText.length >= 2
        }
    });

    const debouncedSetSearchText = debounce((value: string) => {
        setSearchText(value);
    }, 500);

    const handleSearch = (value: string) => {
        debouncedSetSearchText(value);
    };

    const handleChange = (selectedUserId: string, option: { value: string, label: string }) => {
        if (option) {
            onChange(selectedUserId);
        } else {
            setSearchText(selectedUserId || '');
            onChange('');
        }
    };

    useEffect(() => {
        if (data?.data) {
            const formattedUsers = (data as any)?.data?.map((user: { id: string, fullName?: string, username?: string, email?: string }) => ({
                // Show full name alongside email for easier disambiguation.
                value: user.id,
                label: `${user.fullName || user.username || user.email || "Unknown User"}${user.email ? ` (${user.email})` : ""}`,
            }));
            setUserOptions(formattedUsers);
        } else {
            setUserOptions([]);
        }
    }, [data]);

    const searching = (isLoading || isFetching) && searchText.length >= 2;

    return (
        <Select
            allowClear
            showSearch
            placeholder="Search by name..."
            optionFilterProp="label"
            loading={searching}
            onChange={(value: string, option: any) => handleChange(value, option as UserOption)}
            style={{ minWidth: 360, width: "100%", ...style }}
            filterOption={false}
            options={userOptions}
            onSearch={handleSearch}
            notFoundContent={searching ? (
                <div style={{ textAlign: "center", padding: "8px 0" }}><Spin size="small" /></div>
            ) : searchText.length >= 2 ? (
                <>
                    <div style={{ marginBottom: 8 }}>No users found</div>
                    {onInviteRequested ? (
                        <Button
                            type="link"
                            style={{ padding: 0, height: "auto" }}
                            onClick={() => onInviteRequested(searchText.trim())}
                        >
                            {inviteButtonLabel || "Create and send invitation"}
                        </Button>
                    ) : null}
                </>
            ) : (
                "Type at least 2 characters"
            )}
        />
    );
}

export default UserSearchSelect
